/**
 * Share links are built from the URL already in the address bar, so everything that
 * makes a page specific — the selected variant, a collection's filters and sort, a
 * search term, the page number, the locale — travels automatically, whatever scheme
 * the page happens to use to express it. Nothing here needs to know about any of it.
 *
 * What is removed is only sign-in and session state, which is the one category that is
 * actively harmful in a link handed to someone else rather than merely untidy. Shop's
 * sign-in flow is the reported case and leaves a whole `shop_` family behind, so that
 * is matched by prefix and future members are covered without touching this list.
 *
 * Deliberately NOT removed: campaign and ad-click params (utm_*, gclid, fbclid). They
 * make a shared link attribute to the sharer's original click, which is worth fixing,
 * but it is an analytics decision rather than part of this behaviour, and guessing at
 * params we have not seen risks dropping something a recipient needed.
 * Kept in step with the same list in social-share.js.
 */
const SHARE_DENY_PREFIX = "shop_";

const SHARE_DENY_PARAMS = [
  // Sign-in, customer-account and multipass state.
  "_st",
  "login_token",
  "return_to",
  "checkout_url",
  "customer_account_redirect",
  "id_token",
  "code",
  "state",
  "hmac",
  "timestamp",
  // Section Rendering API params. They never reach the address bar on their own, but on
  // a page URL they make the response a bare HTML fragment, so a link carrying one is
  // broken for whoever opens it.
  "section_id",
  "resources[type]",
  // Re-added below from the viewer's own context rather than reflected from the URL.
  "credit_user",
];

/**
 * Drops the params that must not be shared, in place. Keys are snapshotted before
 * deleting, since mutating searchParams while iterating it skips entries.
 * @param {URL} url
 */
function stripUnsharedParams(url) {
  for (const param of [...url.searchParams.keys()]) {
    if (param.startsWith(SHARE_DENY_PREFIX) || SHARE_DENY_PARAMS.includes(param)) {
      url.searchParams.delete(param);
    }
  }
}

/**
 * A credit id is C (customer) or R (UnFranchise repID) followed by digits, mirroring the
 * validation in portal-manager.js. Applied to the credit id a visitor arrived with, so a
 * share link can never reflect an arbitrary value back out.
 */
const CREDIT_USER_PATTERN = /^[CR]\d{1,15}$/;

/**
 * A portal id is alphanumeric with hyphens and underscores, mirroring the validation
 * in portal-manager.js. A cookie value that fails it is treated as absent rather than
 * spliced into a URL path.
 */
const PORTAL_ID_PATTERN = /^[a-zA-Z0-9_-]{1,64}$/;

/**
 * Carries the portal as the first path segment rather than a query param, which is the
 * form the storefront's edge redirect expects: /{portal}/{path} comes back as
 * {path}?portal_id={portal} with the rest of the query string intact.
 *
 * The portal has to sit BEFORE any locale prefix — /portal/zh/products/x resolves,
 * /zh/portal/products/x does not — which is what happens naturally here, since the
 * pathname being prefixed already carries the locale.
 *
 * Kept in step with the same helper in social-share.js.
 *
 * @param {URL} url
 * @param {string} portalId
 */
function applyPortalPath(url, portalId) {
  if (!PORTAL_ID_PATTERN.test(portalId)) return;
  // Only this store's own URLs go through the edge redirect.
  if (url.origin !== window.location.origin) return;

  // The redirect re-adds portal_id itself, so a stale one would duplicate the portal.
  url.searchParams.delete("portal_id");

  const prefix = `/${portalId}`;
  if (url.pathname === prefix || url.pathname.startsWith(`${prefix}/`)) return;

  // The homepage is "/", and the edge expects "/portal" rather than "/portal/".
  url.pathname = url.pathname === "/" ? prefix : prefix + url.pathname;
}

class CopyToClipboard extends HTMLElement {
  /** @type {HTMLElement | null} */
  button = null;

  /** @type {HTMLElement | null} */
  text = null;

  /** @type {string} */
  originalText = "";

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  timeout;

  constructor() {
    super();

    this.button = /** @type {HTMLElement | null} */ (this.querySelector("[data-copy-button]"));
    this.text = /** @type {HTMLElement | null} */ (this.querySelector("[data-copy-text]"));

    if (!this.button) return;

    this.originalText = this.text?.innerText || this.dataset.copyFallbackText || "Copy";

    this.button.addEventListener("click", async () => {
      try {
        await navigator.clipboard.writeText(this.getShareUrl());

        if (this.text) {
          this.text.innerText = this.dataset.copiedText || "Copied!";

          clearTimeout(this.timeout);

          this.timeout = setTimeout(() => {
            if (this.text) this.text.innerText = this.originalText;
          }, 2000);
        }
      } catch (error) {
        console.error("Copy failed:", error);
      }
    });
  }

  /**
   * Build the share URL, keeping only the query params the recipient needs and adding
   * the portal (from cookie) and the credit id (from the ma:customer meta tag, the same
   * source as the social-share block, falling back to the credit id the visitor arrived
   * with so a referral keeps its attribution when a guest passes the link on).
   * @returns {string}
   */
  getShareUrl() {
    const url = new URL(this.dataset.url || window.location.href);

    // Read before stripping: the inbound credit id is dropped along with everything else.
    const inboundCreditUser = url.searchParams.get("credit_user") || "";

    stripUnsharedParams(url);

    const portalId = this.getCookie("portal_id");
    const creditUser = this.getCreditUser() || (CREDIT_USER_PATTERN.test(inboundCreditUser) ? inboundCreditUser : "");

    if (portalId) {
      applyPortalPath(url, portalId);
    }
    if (creditUser) {
      url.searchParams.set("credit_user", creditUser);
    }

    return url.toString();
  }

  /**
   * Read the logged-in customer's referral credit id from the `ma:customer` meta tag
   * (rendered by snippets/meta-tags.liquid). Empty for anonymous customers.
   * @returns {string}
   */
  getCreditUser() {
    const meta = /** @type {HTMLMetaElement | null} */ (document.querySelector('meta[name="ma:customer"]'));
    return meta?.dataset.creditUser || "";
  }

  /**
   * Read a cookie value by name.
   * @param {string} name
   * @returns {string}
   */
  getCookie(name) {
    const match = document.cookie.match(new RegExp("(?:^|;\\s*)" + name + "=([^;]*)"));
    const value = match?.[1];
    return value === undefined ? "" : decodeURIComponent(value);
  }
}

if (!customElements.get("copy-to-clipboard")) {
  customElements.define("copy-to-clipboard", CopyToClipboard);
}
